import React from "react";
import { connect } from "react-redux";
import { Message, Dropdown, Segment, Checkbox, Input, Icon } from "semantic-ui-react";
import Validator from "validator";
import api from "../../api";
import Switch from "react-switch";
import utilFunc from "../../util_funs";
import { Form } from "formsy-semantic-ui-react";
import "./integration.css"
import ConfirmDialog from "../Modal/ConfirmDialog";
import validationRule from "../../validation";
import GridList from "../Grid";
import OutboundVoiceWebhook from "./OutboundVoiceWebhook";
import OutboundSmsWebhook from "./OutboundSmsWebhook";
import { resetOutbound, hydrateOutbound } from "../../store/outboundVoiceSlice";
import { resetOutboundSms, hydrateOutboundSms } from "../../store/outboundSmsSlice";
import config from "../../config";
import {
  WEBHOOK_TIME_FILTER_FIELD,
  validateWebhookTimeThresholdSeconds,
} from "./webhookTimeFilterConstants";

// ============================================================================
// HELPER FUNCTIONS - Reusable utilities to reduce code duplication
// ============================================================================

/**
 * Fetch VOIP parsed configuration
 * Centralized function to avoid duplication across handleSwitchVoip, editCredentials, createNew
 * Returns empty array on any error with detailed logging
 */
const fetchVoipParsedValues = async () => {
  try {
    const voipRes = await api.manageUser.getVoipParseConfiguration({});

    // Validate response structure
    if (!voipRes) {
      console.error("fetchVoipParsedValues: No response from API");
      return [];
    }

    if (!voipRes.data) {
      console.warn("fetchVoipParsedValues: Response missing data field");
      return [];
    }

    const responseData = voipRes.data;

    // Check if parsed_values exists
    if (responseData.parsed_values === undefined || responseData.parsed_values === null) {
      console.warn("fetchVoipParsedValues: parsed_values is undefined/null");
      return [];
    }

    // Validate it's an array
    if (!Array.isArray(responseData.parsed_values)) {
      console.error("fetchVoipParsedValues: parsed_values is not an array:", typeof responseData.parsed_values);
      return [];
    }

    // Filter out invalid entries
    const validParsedValues = responseData.parsed_values.filter(item =>
      item && typeof item === 'object' && item.key
    );

    if (validParsedValues.length !== responseData.parsed_values.length) {
      console.warn(`fetchVoipParsedValues: Filtered out ${responseData.parsed_values.length - validParsedValues.length} invalid entries`);
    }

    return validParsedValues;

  } catch (err) {
    console.error("Error fetching VOIP parse configuration:", err.message || err);
    return [];
  }
};

/**
 * Parse filter data (handles both JSON strings and arrays)
 * Provides consistent error handling for all filter types
 * Returns empty array for any invalid input
 */
const parseFilters = (filters, filterType = "unknown") => {
  // Handle null/undefined/empty cases
  if (filters === null || filters === undefined) {
    console.warn(`${filterType} filters is null/undefined, returning empty array`);
    return [];
  }

  try {
    // Already an array - validate and return
    if (Array.isArray(filters)) {
      // Validate array contents
      if (filters.length === 0) {
        return [];
      }
      // Outbound: each item is { workspace_id, fields, ... } — not { field, selectedValue }
      const isOutbound = filterType === "outbound";
      const validFilters = filters.filter((f) => {
        if (!f || typeof f !== "object") return false;
        if (isOutbound) {
          return f.workspace_id !== undefined && f.workspace_id !== null;
        }
        return Boolean(f.field);
      });
      if (validFilters.length !== filters.length) {
        console.warn(`${filterType} filters contained invalid entries, filtered ${filters.length - validFilters.length} items`);
      }
      return validFilters;
    }

    // String - attempt to parse
    if (typeof filters === 'string') {
      const trimmed = filters.trim();

      // Empty string
      if (trimmed === '') {
        return [];
      }

      // Check if it looks like valid JSON
      if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) {
        console.error(`${filterType} filters: Invalid JSON format (doesn't start with [ or {):`, trimmed.substring(0, 50));
        return [];
      }

      const parsed = JSON.parse(trimmed);

      // Validate parsed result
      if (!Array.isArray(parsed)) {
        console.error(`${filterType} filters: Parsed JSON is not an array:`, typeof parsed);
        return [];
      }

      const isOutbound = filterType === "outbound";
      return parsed.filter((f) => {
        if (!f || typeof f !== "object") return false;
        if (isOutbound) {
          return f.workspace_id !== undefined && f.workspace_id !== null;
        }
        return Boolean(f.field);
      });
    }

    // Unexpected type
    console.error(`${filterType} filters: Unexpected type ${typeof filters}, returning empty array`);
    return [];

  } catch (e) {
    console.error(`Error parsing ${filterType} filters:`, e.message, 'Input:',
      typeof filters === 'string' ? filters.substring(0, 100) : filters);
    return [];
  }
};

/**
 * Build dynamic dropdown options from parsed values and selected fields
 * Used for voice, web, and VOIP filter building
 * Returns empty object if inputs are invalid
 */
const buildDynamicOptions = (selectedFields, parsedValues) => {
  const dynamicOptions = {};

  // Validate inputs
  if (!selectedFields || !Array.isArray(selectedFields) || selectedFields.length === 0) {
    return dynamicOptions;
  }

  if (!parsedValues || !Array.isArray(parsedValues) || parsedValues.length === 0) {
    console.warn('buildDynamicOptions: parsedValues is empty or invalid');
    return dynamicOptions;
  }

  selectedFields.forEach((field) => {
    // Skip invalid field names
    if (!field || typeof field !== 'string' || field.trim() === '') {
      console.warn('buildDynamicOptions: Skipping invalid field:', field);
      return;
    }

    const parsedField = parsedValues.find((item) => item && item.key === field);

    if (!parsedField) {
      console.warn(`buildDynamicOptions: No parsed field found for "${field}"`);
      return;
    }

    // Validate values array exists and has content
    if (!parsedField.val || !Array.isArray(parsedField.val)) {
      console.warn(`buildDynamicOptions: Field "${field}" has no values array`);
      return;
    }

    if (parsedField.val.length === 0) {
      console.warn(`buildDynamicOptions: Field "${field}" has empty values array`);
      return;
    }

    // Build options, filtering out invalid values
    dynamicOptions[field] = parsedField.val
      .filter(option => option !== null && option !== undefined && String(option).trim() !== '')
      .map((option, idx) => ({
        key: `${field}-${idx}`,
        text: String(option),
        value: String(option).trim(),
      }));
  });

  return dynamicOptions;
};

/**
 * Get preferred field from potentially duplicate fields (for VOIP)
 * Prioritizes fields with non-empty value arrays
 */
const getPreferredField = (fieldKey, parsedValues) => {
  const matchingFields = parsedValues?.filter((item) => item.key === fieldKey) || [];

  if (matchingFields.length === 0) return null;
  if (matchingFields.length === 1) return matchingFields[0];

  // Multiple matches - prefer the one with options
  const fieldWithOptions = matchingFields.find(
    (f) => Array.isArray(f.val) && f.val.length > 0
  );
  return fieldWithOptions || matchingFields[0];
};

/**
 * Build dynamic options for VOIP fields (handles duplicate field names)
 * Returns empty object if inputs are invalid
 */
const buildVoipDynamicOptions = (selectedFields, voipParsedValues) => {
  const dynamicOptions = {};

  // Validate inputs
  if (!selectedFields || !Array.isArray(selectedFields) || selectedFields.length === 0) {
    return dynamicOptions;
  }

  if (!voipParsedValues || !Array.isArray(voipParsedValues) || voipParsedValues.length === 0) {
    console.warn('buildVoipDynamicOptions: voipParsedValues is empty or invalid');
    return dynamicOptions;
  }

  selectedFields.forEach((field) => {
    // Skip invalid field names
    if (!field || typeof field !== 'string' || field.trim() === '') {
      console.warn('buildVoipDynamicOptions: Skipping invalid field:', field);
      return;
    }

    const parsedField = getPreferredField(field, voipParsedValues);

    if (!parsedField) {
      console.warn(`buildVoipDynamicOptions: No parsed field found for "${field}"`);
      return;
    }

    // Validate values array exists and has content
    if (!parsedField.val || !Array.isArray(parsedField.val)) {
      console.warn(`buildVoipDynamicOptions: Field "${field}" has no values array`);
      return;
    }

    if (parsedField.val.length === 0) {
      console.warn(`buildVoipDynamicOptions: Field "${field}" has empty values array`);
      return;
    }

    // Build options, filtering out invalid values
    dynamicOptions[field] = parsedField.val
      .filter(option => option !== null && option !== undefined && String(option).trim() !== '')
      .map((option, idx) => ({
        key: `voip-${field}-${idx}`,
        text: String(option),
        value: String(option).trim(),
      }));
  });

  return dynamicOptions;
};

/**
 * Process filters for edit mode (parse + extract + build options)
 * Combines multiple operations into a single reusable function
 * Returns safe defaults if any step fails
 */
const processFiltersForEdit = (rawFilters, parsedValues, filterType = "voice") => {
  // Initialize safe defaults
  const safeDefaults = {
    selectedWebhook: [],
    selectedValues: {},
    dynamicOptions: {}
  };

  try {
    // Parse filters with validation
    const parsedFilters = parseFilters(rawFilters, filterType);

    if (!Array.isArray(parsedFilters) || parsedFilters.length === 0) {
      console.warn(`processFiltersForEdit (${filterType}): No valid filters after parsing`);
      return safeDefaults;
    }

    // Extract field names (with validation)
    const selectedWebhook = parsedFilters
      .map((filter) => filter?.field)
      .filter(field => field && typeof field === 'string' && field.trim() !== '');

    // Extract selected values
    const selectedValues = parsedFilters.reduce((acc, filter) => {
      if (filter?.field) {
        acc[filter.field] = filter.selectedValue || [];
      }
      return acc;
    }, {});

    // Voice: synthetic time field is not in parsed_values — exclude from dynamic dropdown build
    const fieldsForDynamic =
      filterType === "voice"
        ? selectedWebhook.filter((k) => k !== WEBHOOK_TIME_FILTER_FIELD)
        : selectedWebhook;
    const dynamicOptions = buildDynamicOptions(fieldsForDynamic, parsedValues || []);

    return { selectedWebhook, selectedValues, dynamicOptions };
  } catch (err) {
    console.error(`processFiltersForEdit (${filterType}): Unexpected error:`, err);
    return safeDefaults;
  }
};

/**
 * Process VOIP filters for edit mode (handles duplicates)
 * Returns safe defaults if any step fails
 */
const processVoipFiltersForEdit = (rawFilters, voipParsedValues) => {
  // Initialize safe defaults
  const safeDefaults = {
    selectedWebhook: [],
    selectedValues: {},
    dynamicOptions: {}
  };

  try {
    // Parse filters with validation
    const parsedFilters = parseFilters(rawFilters, "voip");

    if (!Array.isArray(parsedFilters) || parsedFilters.length === 0) {
      console.warn('processVoipFiltersForEdit: No valid filters after parsing');
      return safeDefaults;
    }

    // Extract field names (with validation)
    const selectedWebhook = parsedFilters
      .map((filter) => filter?.field)
      .filter(field => field && typeof field === 'string' && field.trim() !== '');

    // Extract selected values
    const selectedValues = parsedFilters.reduce((acc, filter) => {
      if (filter?.field) {
        acc[filter.field] = filter.selectedValue || [];
      }
      return acc;
    }, {});

    // Build dynamic options (with empty array guard)
    const dynamicOptions = buildVoipDynamicOptions(selectedWebhook, voipParsedValues || []);

    return { selectedWebhook, selectedValues, dynamicOptions };
  } catch (err) {
    console.error('processVoipFiltersForEdit: Unexpected error:', err);
    return safeDefaults;
  }
};

/**
 * Normalize boolean/string values to boolean
 */
const normalizeBoolean = (value) => {
  return value === true || value === "1" || value === 1 || value === "on";
};

/**
 * Validate field has a non-empty key
 */
const isValidField = (field) => {
  return field &&
         field.key &&
         typeof field.key === 'string' &&
         field.key.trim() !== '';
};

/**
 * Deduplicate and validate fields by key, keeping entries with options
 * Filters out null/empty/invalid field names
 */
const deduplicateFields = (parsedValues) => {
  if (!parsedValues || !Array.isArray(parsedValues)) return [];

  const uniqueFields = {};

  parsedValues.forEach((field) => {
    // Skip invalid fields
    if (!isValidField(field)) {
      console.warn('Skipping invalid field:', field);
      return;
    }

    const fieldKey = field.key.trim();

    if (!uniqueFields[fieldKey]) {
      uniqueFields[fieldKey] = { ...field, key: fieldKey };
    } else {
      const existingHasOptions =
        Array.isArray(uniqueFields[fieldKey].val) && uniqueFields[fieldKey].val.length > 0;
      const newHasOptions = Array.isArray(field.val) && field.val.length > 0;

      if (newHasOptions && !existingHasOptions) {
        uniqueFields[fieldKey] = { ...field, key: fieldKey };
      }
    }
  });

  return Object.values(uniqueFields);
};

/**
 * Legacy alias for backward compatibility
 */
const deduplicateVoipFields = deduplicateFields;


class Webhook extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      modal: false,
      loading: false,
      webhook: "",
      webhookStatus: false,
      successMessage: null,
      errors: null,
      contentType: "multipart/form-data",
      workspaceId: null,
      web_workspaceId: null,
      voip_config_id: null,
      workspaceBranches: null,
      web_workspaceBranches: null,
      voip_branches: null,
      parsed_values: null,
      web_parsed_values: null,
      voip_parsed_values: null,
      selectedWebhook: [],
      dynamicOptions: {}, // To store the options for each field dynamically
      selectedValues: {}, // To store selected values for dynamic dropdowns,
      web_dynamicOptions: {}, // To store the options for each field dynamically
      web_selectedValues: {}, // To store selected values for dynamic dropdowns
      voip_dynamicOptions: {}, // To store the options for each VOIP field dynamically
      voip_selectedValues: {}, // To store selected values for VOIP dynamic dropdowns
      voip_selectedWebhook: [], // Selected VOIP webhook rules
      required_settings: 0,
      voip_required_setting: 0,
      confirmDialog:false,
      webhook_id:null,
      errorMessage: null,
      dataLoading: null,
      webhook_info:{},
      refreshGrid:false,
      disable: false,
      voice_indices:[],
      web_indices:[],
      fallback_webhook_exists:false,
      voiceToggleLoading: false,
      webToggleLoading: false,
      voipToggleLoading: false

    };
    this.formRef = React.createRef();
    this.errorRef = React.createRef();
    this.inboundTimeFilterRef = React.createRef();

    // Debounce timers for toggle switches
    this.voiceToggleTimer = null;
    this.webToggleTimer = null;
    this.voipToggleTimer = null;
        this.columnLists = [
          { text: "S.No", key: "id", sort: false },
          { text: "Webhook Url", key: "webhook_url", sort: false },
          {text: "Status", key: "status"},
          { text: "Type", key: "content_type" },
          {text: "Inbound Voice", key: "voice_on"},
          {text: "Outbound Voice", key: "outbound_voice_on"},
          // { text: "Voice", key: "required_setting" },
          {text: "Chat", key: "web_on"},
          // { text: "Chat", key: "web_required_setting" },
          {text: "VOIP", key: "voip_on"},
          {text: "Outbound SMS", key: "outbound_sms_on"},
          // {text: "VOIP", key: "voip_required_setting"},
          { text: "Is Fallback", key: "fallback_webhook" },
          {
            text: "Action",
            key: "action",
            sort: false,
            event: [
                {
                    key: "editCredentials",
                    params: ["id"],
                    func: (id) =>
                        this.editCredentials(id),
                },
                {
                    key: "deleteCredentials",
                    params: ["id"],
                    func: (id) => this.confirmAction(id),
                },
            ],
        },
        ];
  }
  confirmAction = (id) =>
    this.setState({ webhook_id:id, confirmDialog: true });
  toggleConfirmModal = () =>
    this.setState({ confirmDialog: false, webhook_id: 0 });

  // Copy to clipboard functionality
  copyToClipboard = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        this.setState({
          successMessage: 'Webhook URL copied to clipboard!',
        });
        setTimeout(() => {
          this.setState({ successMessage: null });
        }, 2000);
      }).catch((err) => {
        console.error('Failed to copy:', err);
        this.fallbackCopyToClipboard(text);
      });
    } else {
      this.fallbackCopyToClipboard(text);
    }
  };

  // Fallback copy method for older browsers
  fallbackCopyToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      this.setState({
        successMessage: 'Webhook URL copied to clipboard!',
      });
      setTimeout(() => {
        this.setState({ successMessage: null });
      }, 2000);
    } catch (err) {
      console.error('Fallback copy failed:', err);
    }
    document.body.removeChild(textArea);
  };

  /** Remove one validation key; keep other field errors visible. */
  omitFormErrorKey = (prevErrors, key) => {
    if (!prevErrors?.[key]) return prevErrors;
    const next = { ...prevErrors };
    delete next[key];
    return Object.keys(next).length ? next : null;
  };

  handleChange = (e) => {
    const webhook_info = {
      ...this.state.webhook_info,
      [e.target.name]: e.target.value
    };

    this.setState((prev) => ({
      webhook_info,
      disable: false,
      errors: prev.errors?.webhook
        ? this.omitFormErrorKey(prev.errors, "webhook")
        : prev.errors,
    }));
  };

  clearFormErrorKey = (key) => {
    this.setState((prev) => {
      const next = this.omitFormErrorKey(prev.errors, key);
      if (next === prev.errors) return null;
      return { errors: next };
    });
  };

  scrollToFirstWebhookFormError = (errors) => {
    const scrollOpts = { behavior: "smooth", block: "center" };
    const scroll = (el) => el?.scrollIntoView(scrollOpts);

    if (errors.webhook) return scroll(this.errorRef.current);
    if (errors.voice_time_threshold) {
      scroll(this.inboundTimeFilterRef.current);
      const inboundTimeInput = document.getElementById("webhook-inbound-voice-time-seconds");
      if (inboundTimeInput) inboundTimeInput.focus();
      return;
    }
    const obKey = Object.keys(errors).find((k) => k.startsWith("outbound_time_"));
    if (!obKey) return;
    const input = document.getElementById(`webhook-outbound-voice-time-${obKey.replace(/^outbound_time_/, "")}`);
    scroll(input?.closest(".webhook-time-based-filter-block") || input);
    if (input) input.focus();
  };

  handleSubmit = () => {
    const errors = this.validateForm();
    this.setState({ errors }, () => {
      // After state is set, scroll to error message
      if (Object.keys(errors).length > 0) {
        setTimeout(() => this.scrollToFirstWebhookFormError(errors), 100);
      }
    });

    if (Object.keys(errors).length > 0) {
      return;
    }

    if (Object.keys(errors).length === 0) {
      this.setState({ disable:true});
      let { status, webhook_url, content_type, selectedWebhook,
        selectedValues, web_selectedWebhook,
        web_selectedValues, voip_selectedWebhook,
        voip_selectedValues} = this.state.webhook_info;

      // Construct filters array and convert to JSON string
      const filters =
        selectedWebhook.map((key) => ({
          field: key,
          selectedValue: selectedValues[key] || null, // Attach selected values for each key
        }));
        // this.setState({loading: true});
        const web_filters =
        web_selectedWebhook.map((key) => ({
          field: key,
          selectedValue: web_selectedValues[key] || null, // Attach selected values for each key
        }));

        // Construct VOIP filters
        const voip_filters =
        (voip_selectedWebhook || []).map((key) => ({
          field: key,
          selectedValue: voip_selectedValues[key] || null, // Attach selected values for each key
        }));

      // Construct outbound filters for each workspace
      const { outboundVoice, outboundSms } = this.props;
      const outbound_filters = buildOutboundFilters(outboundVoice);
      const outbound_sms_filters = outboundSms.enabled
        ? buildOutboundFilters(outboundSms).map((ws) => ({
            ...ws,
            fields: (ws.fields || []).filter(
              (f) => f.field !== WEBHOOK_TIME_FILTER_FIELD
            ),
          }))
        : [];

      // Construct payload
      const payload = {
        id:this.state.webhook_id,
        webhookStatus:status,
        webhook: webhook_url,
        content_type,
        filters,
        web_filters,
        voip_filters,
        outbound_voice_on: outboundVoice.enabled,
        outbound_filters,
        outbound_sms_on: outboundSms.enabled,
        outbound_sms_filters,
        required_setting: this.state.webhook_info.required_setting,
        web_required_setting: this.state.webhook_info.web_required_setting,
        voip_required_setting: this.state.webhook_info.voip_required_setting,
        // ✓ REFACTORED: Use normalizeBoolean helper for consistency
        voice_on: normalizeBoolean(this.state.webhook_info.voice_on),
        voip_on: normalizeBoolean(this.state.webhook_info.voip_on),
        web_on: normalizeBoolean(this.state.webhook_info.web_on),
        voice_indices: this.state.voice_indices,
        web_indices: this.state.web_indices,
        fallback_webhook: normalizeBoolean(this.state.webhook_info.fallback_webhook)
      };


      // if (!status) webhook_url = "";
      api.webhook.createWebhook(payload).then((resp) => {
        if (resp && resp.message) {
          // Reset outbound voice state after successful save
          this.props.resetOutbound();
          this.props.resetOutboundSms();
          // ✓ FIXED: Consistent naming (webhook_info)
          this.setState({
            successMessage: resp.message,
            errorMessage: null,
            modal: false,
            loading: false,
            errors: null,
            webhook_info: {},
            webhook_id: 0,
            refreshGrid: true,
            disable: false
          }, () => {
            utilFunc.scrollTop(800);
            setTimeout(() => {
              this.setState({ successMessage: null, refreshGrid: false, disable: false });
            }, 5000);
          });
        }
      });
    }
  };

  validateForm() {
    const errors = {};
    // if (this.state.webhook_info.status) {
      if (!this.state.webhook_info.webhook_url) errors.webhook = "This field is required.";
      else if (!Validator.isURL(this.state.webhook_info.webhook_url)) {
        errors.webhook = "Invalid webhook url.";
      }
    // }
    const voiceSel = this.state.webhook_info.selectedWebhook || [];
    if (voiceSel.includes(WEBHOOK_TIME_FILTER_FIELD)) {
      const raw = (this.state.webhook_info.selectedValues[WEBHOOK_TIME_FILTER_FIELD] || [])[0];
      const err = validateWebhookTimeThresholdSeconds(raw);
      if (err) errors.voice_time_threshold = err;
    }

    const ov = this.props.outboundVoice;
    if (ov?.enabled && Array.isArray(ov.selectedWorkspaces) && ov.selectedWorkspaces.length > 0) {
      ov.selectedWorkspaces.forEach((wsId) => {
        const pf = ov.parsedFields?.[wsId] || [];
        if (pf.includes(WEBHOOK_TIME_FILTER_FIELD)) {
          const raw = ov.selectedValues?.[wsId]?.[WEBHOOK_TIME_FILTER_FIELD]?.[0];
          const err = validateWebhookTimeThresholdSeconds(raw);
          if (err) errors[`outbound_time_${wsId}`] = err;
        }
      });
    }

    return errors;
  }
  handleSwitch = (checked) => {
    // ✓ FIXED: Clone state before updating
    const webhook_info = {
      ...this.state.webhook_info,
      status: checked
    };
    this.setState({ webhook_info });
  };

  handleSwitchvoice = (checked) => {
    // Prevent rapid toggling while loading
    if (this.state.voiceToggleLoading) {
      console.warn('Voice toggle already in progress, ignoring');
      return;
    }

    // Clear any pending debounce timer
    if (this.voiceToggleTimer) {
      clearTimeout(this.voiceToggleTimer);
    }

    // ✓ FIXED: Clone state before updating
    const webhook_info = {
      ...this.state.webhook_info,
      voice_on: checked
    };

    // If voice is enabled and we don't have the parsed values yet, fetch them
    if (checked && !this.state.parsed_values) {
      // Set loading state
      this.setState({ voiceToggleLoading: true });

      // Debounce the API call
      this.voiceToggleTimer = setTimeout(() => {
        api.manageUser.getVoiceWorkspaceBranch({})
          .then((res) => {
            // Validate response structure
            if (!res || !res.status) {
              console.error("Invalid response from getVoiceWorkspaceBranch:", res);
              this.setState({
                webhook_info,
                voiceToggleLoading: false,
                errorMessage: "Failed to load voice workspace data"
              });
              return;
            }

            if (!res.data) {
              console.error("Response missing data field");
              this.setState({ webhook_info, voiceToggleLoading: false });
              return;
            }

            this.setState({
              webhook_info,
              workspaceId: res.data.workspaceId,
              workspaceBranches: Array.isArray(res.data.data) ? res.data.data : [],
              voice_type: res.data?.type,
              parsed_values: Array.isArray(res.data?.parsed_values) ? res.data.parsed_values : [],
              web_workspaceId: res.data?.webparsedetail?.workspaceId,
              web_workspaceBranches: Array.isArray(res.data?.webparsedetail?.data) ? res.data.webparsedetail.data : [],
              web_parsed_values: Array.isArray(res.data?.webparsedetail?.parsed_values) ? res.data.webparsedetail.parsed_values : [],
              voice_indices: Array.isArray(res.data?.key_indices) ? res.data.key_indices : [],
              web_indices: Array.isArray(res.data?.webparsedetail?.key_indices) ? res.data.webparsedetail.key_indices : [],
              voiceToggleLoading: false
            });
          })
          .catch((err) => {
            console.error("Error fetching workspace branch data:", err);
            this.setState({
              webhook_info,
              voiceToggleLoading: false,
              errorMessage: "Failed to load voice workspace data"
            });
            setTimeout(() => {
              this.setState({ errorMessage: null });
            }, 5000);
          });
      }, 300); // 300ms debounce
    } else {
      this.setState({ webhook_info });
    }
  };
  handleSwitchVoip = (checked) => {
    // Prevent rapid toggling while loading
    if (this.state.voipToggleLoading) {
      console.warn('VOIP toggle already in progress, ignoring');
      return;
    }

    // Clear any pending debounce timer
    if (this.voipToggleTimer) {
      clearTimeout(this.voipToggleTimer);
    }

    // ✓ FIXED: Clone state before updating
    const webhook_info = {
      ...this.state.webhook_info,
      voip_on: checked
    };

    // If VOIP is enabled, fetch the parsed values (if not already loaded)
    if (checked && (!this.state.voip_parsed_values || this.state.voip_parsed_values.length === 0)) {
      // Set loading state
      this.setState({ voipToggleLoading: true });

      // Debounce the API call
      this.voipToggleTimer = setTimeout(async () => {
        try {
          // ✓ REFACTORED: Use centralized helper function
          const voip_parsed_values = await fetchVoipParsedValues();
          this.setState({
            webhook_info,
            voip_parsed_values,
            voipToggleLoading: false
          });
        } catch (err) {
          console.error("Error in handleSwitchVoip:", err);
          this.setState({
            webhook_info,
            voipToggleLoading: false,
            errorMessage: "Failed to load VOIP configuration"
          });
          setTimeout(() => {
            this.setState({ errorMessage: null });
          }, 5000);
        }
      }, 300); // 300ms debounce
    } else {
      this.setState({ webhook_info });
    }
  };
  handleSwitchweb = (checked) => {
    // Prevent rapid toggling while loading
    if (this.state.webToggleLoading) {
      console.warn('Web toggle already in progress, ignoring');
      return;
    }

    // Clear any pending debounce timer
    if (this.webToggleTimer) {
      clearTimeout(this.webToggleTimer);
    }

    // ✓ FIXED: Clone state before updating
    const webhook_info = {
      ...this.state.webhook_info,
      web_on: checked
    };

    // If web is enabled and we don't have the parsed values yet, fetch them
    if (checked && !this.state.web_parsed_values) {
      // Set loading state
      this.setState({ webToggleLoading: true });

      // Debounce the API call
      this.webToggleTimer = setTimeout(() => {
        api.manageUser.getVoiceWorkspaceBranch({})
          .then((res) => {
            // Validate response structure
            if (!res || !res.status) {
              console.error("Invalid response from getVoiceWorkspaceBranch:", res);
              this.setState({
                webhook_info,
                webToggleLoading: false,
                errorMessage: "Failed to load chat workspace data"
              });
              return;
            }

            if (!res.data) {
              console.error("Response missing data field");
              this.setState({ webhook_info, webToggleLoading: false });
              return;
            }

            this.setState({
              webhook_info,
              workspaceId: res.data.workspaceId,
              workspaceBranches: Array.isArray(res.data.data) ? res.data.data : [],
              voice_type: res.data?.type,
              parsed_values: Array.isArray(res.data?.parsed_values) ? res.data.parsed_values : [],
              web_workspaceId: res.data?.webparsedetail?.workspaceId,
              web_workspaceBranches: Array.isArray(res.data?.webparsedetail?.data) ? res.data.webparsedetail.data : [],
              web_parsed_values: Array.isArray(res.data?.webparsedetail?.parsed_values) ? res.data.webparsedetail.parsed_values : [],
              voice_indices: Array.isArray(res.data?.key_indices) ? res.data.key_indices : [],
              web_indices: Array.isArray(res.data?.webparsedetail?.key_indices) ? res.data.webparsedetail.key_indices : [],
              webToggleLoading: false
            });
          })
          .catch((err) => {
            console.error("Error fetching workspace branch data:", err);
            this.setState({
              webhook_info,
              webToggleLoading: false,
              errorMessage: "Failed to load chat workspace data"
            });
            setTimeout(() => {
              this.setState({ errorMessage: null });
            }, 5000);
          });
      }, 300); // 300ms debounce
    } else {
      this.setState({ webhook_info });
    }
  };                                               
  HandleRequiredSetting = (checked) => {
    // ✓ FIXED: Clone state before updating
    const webhook_info = {
      ...this.state.webhook_info,
      required_setting: checked ? 1 : 0
    };
    this.setState({ webhook_info, disable: false });
  };
  HandleRequiredSettingforweb=(checked) => {
    let webhook={...this.state.webhook_info} ;
    webhook.web_required_setting=checked ? 1:0;
    this.setState({ webhook_info:webhook,disable:false});
  }
  HandleRequiredSettingforvoip=(checked) => {
    let webhook={...this.state.webhook_info} ;
    webhook.voip_required_setting=checked ? 1:0;
    this.setState({ webhook_info:webhook,disable:false});
  }
  // ✓ REFACTORED: Removed console.logs, improved readability
  fallbackWebhook = async (checked) => {
    const webhook_info = { ...this.state.webhook_info };

    if (checked) {
      this.setState({ formLoading: true });
      const fallback_webhook_exists = await this.checkFallbackExistsk(webhook_info.id);

      if (fallback_webhook_exists) {
        webhook_info.fallback_webhook = 0;
        this.setState({
          fallback_webhook_exists: true,
          webhook_info,
          disable: false,
          formLoading: false
        });
      } else {
        webhook_info.fallback_webhook = checked ? 1 : 0;
        this.setState({
          fallback_webhook_exists: false,
          webhook_info,
          disable: false,
          formLoading: false
        });
      }
    } else {
      webhook_info.fallback_webhook = 0;
      this.setState({
        fallback_webhook_exists: false,
        webhook_info,
        disable: false,
        formLoading: false
      });
    }

    setTimeout(() => {
      this.setState({ formLoading: false, fallback_webhook_exists: false });
    }, 5000);
  };

  // ✓ REFACTORED: Removed console.logs, improved error handling
  checkFallbackExistsk = (id = null) => {
    return new Promise((resolve) => {
      api.webhook.checkFallbackExists({ id })
        .then((res) => {
          if (res.status) {
            this.setState({ loading: false });
            resolve(true);
          } else {
            resolve(false);
          }
        })
        .catch((err) => {
          console.error("Error checking fallback webhook:", err);
          resolve(false);
        });
    });
  };
  // Handle dropdown change
  handleDropdownChange = (e, { value }) => {
    // ✓ FIXED: Clone state before updating
    const webhook_info = {
      ...this.state.webhook_info,
      content_type: value
    };
    this.setState({ webhook_info, disable: false });
  };
  componentDidMount() {
    // Add global copy function for webhook URLs
    window.copyWebhookUrl = (url) => {
      this.copyToClipboard(url);
    };
  }

  componentWillUnmount() {
    // Cleanup global function
    if (window.copyWebhookUrl) {
      delete window.copyWebhookUrl;
    }

    // Clear debounce timers
    if (this.voiceToggleTimer) {
      clearTimeout(this.voiceToggleTimer);
    }
    if (this.webToggleTimer) {
      clearTimeout(this.webToggleTimer);
    }
    if (this.voipToggleTimer) {
      clearTimeout(this.voipToggleTimer);
    }
  }
  
  openPage() {
    window.open(
      "https://docs.google.com/document/d/1S0lzB7wsoTVpjsEhlnoAEIlPtY_XWnNer1zBmhcnax0/edit?usp=sharing"
    );
  }
  // ✓ REFACTORED: Simplified using consistent pattern with validation
  branchOption = () => {
    const timeOption = {
      key: "voice-time-filter",
      text: "Time-based",
      value: WEBHOOK_TIME_FILTER_FIELD,
    };

    if (!this.state.parsed_values || !Array.isArray(this.state.parsed_values)) {
      return [timeOption];
    }

    const options = this.state.parsed_values
      .filter(item => item && item.key && typeof item.key === 'string' && item.key.trim() !== '')
      .map((item, index) => ({
        key: `voice-field-${index}`,
        text: item.key,
        value: item.key,
      }));

    if (options.length === 0) {
      return [timeOption];
    }

    return [timeOption, ...options];
  };

  web_branchOption = () => {
    if (!this.state.web_parsed_values || !Array.isArray(this.state.web_parsed_values)) {
      return [];
    }

    if (this.state.web_parsed_values.length === 0) {
      return [];
    }

    // Filter out invalid entries and map to dropdown options
    const options = this.state.web_parsed_values
      .filter(item => item && item.key && typeof item.key === 'string' && item.key.trim() !== '')
      .map((item, index) => ({
        key: `web-field-${index}`,
        text: item.key,
        value: item.key,
      }));

    // Add header as first item
    if (options.length > 0) {
      return [
        {
          key: 'header-web',
          text: 'Name',
          value: 'header-web',
          disabled: true,
          className: 'webhook-dropdown-header'
        },
        ...options
      ];
    }

    return options;
  };
  // ✓ REFACTORED: Use buildDynamicOptions helper to reduce duplication
  handleWebhookRule = (e, d) => {
    const selectedKeys = d.value.filter(val => !val.startsWith("header-"));
    const keysForDynamic = selectedKeys.filter((k) => k !== WEBHOOK_TIME_FILTER_FIELD);
    const dynamicOptions = buildDynamicOptions(keysForDynamic, this.state.parsed_values);

    let selectedValues = { ...this.state.webhook_info.selectedValues };
    if (!selectedKeys.includes(WEBHOOK_TIME_FILTER_FIELD)) {
      delete selectedValues[WEBHOOK_TIME_FILTER_FIELD];
    }

    const webhook_info = {
      ...this.state.webhook_info,
      selectedWebhook: selectedKeys,
      dynamicOptions,
      selectedValues,
    };

    this.setState({ webhook_info, disable: false });
  };

  handleVoiceTimeThresholdChange = (value) => {
    const webhook_info = {
      ...this.state.webhook_info,
      selectedValues: {
        ...this.state.webhook_info.selectedValues,
        [WEBHOOK_TIME_FILTER_FIELD]:
          value === "" || value === undefined ? [] : [String(value).trim()],
      },
    };
    this.setState((prev) => ({
      webhook_info,
      disable: false,
      errors: prev.errors?.voice_time_threshold
        ? this.omitFormErrorKey(prev.errors, "voice_time_threshold")
        : prev.errors,
    }));
  };

  handleweb_WebhookRule = (e, d) => {
    // Filter out header items (they start with 'header-')
    const selectedKeys = d.value.filter(val => !val.startsWith('header-'));
    const dynamicOptions = buildDynamicOptions(selectedKeys, this.state.web_parsed_values);

    const webhook_info = {
      ...this.state.webhook_info,
      web_selectedWebhook: selectedKeys,
      web_dynamicOptions: dynamicOptions
    };

    this.setState({ webhook_info, disable: false });
  };

  // Render additional dropdowns dynamically based on selected items
  renderDynamicDropdowns = () => {
    if (!this.state.webhook_info.selectedWebhook || !Array.isArray(this.state.webhook_info.selectedWebhook)) {
      return null;
    }
    return this.state.webhook_info.selectedWebhook.map((key) => {
      if (key === WEBHOOK_TIME_FILTER_FIELD) {
        const raw = (this.state.webhook_info.selectedValues[key] || [])[0] ?? "";
        const timeErr = Boolean(this.state.errors?.voice_time_threshold);
        return (
          <div
            className="webhook-filter-rule webhook-time-threshold-field webhook-time-based-filter-block"
            key={key}
            ref={this.inboundTimeFilterRef}
          >
            <div className="webhook-time-based-filter-header">
              <div className="webhook-time-based-filter-icon-wrap" aria-hidden="true">
                <Icon name="clock outline" />
              </div>
              <div className="webhook-time-based-filter-header-text">
                <div className="webhook-time-based-filter-title">Time-based filter</div>
                <p className="webhook-time-based-filter-desc">
                  Webhook fires only when a call exceeds a set duration threshold.
                </p>
              </div>
            </div>
            <Form.Field className="webhook-time-based-filter-form-field">
              <div className="webhook-time-based-filter-card">
                <div className="webhook-time-based-filter-card-heading">Rule configuration</div>
                <div className="webhook-time-rule-inline">
                  <span className="webhook-time-rule-lead">Webhook will trigger if the call exceeds</span>
                  <span
                    className={
                      "webhook-time-rule-input-cluster" + (timeErr ? " webhook-time-rule-input-cluster--error" : "")
                    }
                  >
                    <Input
                      id="webhook-inbound-voice-time-seconds"
                      className="webhook-time-rule-numeric-input"
                      type="number"
                      input={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      style={{ padding: "0px 6px 0px 6px" }}
                      min={0}
                      value={raw}
                      onChange={(e, d) => this.handleVoiceTimeThresholdChange(d.value)}
                    />
                    <span className="webhook-time-rule-unit-s">s</span>
                  </span>
                  <span className="webhook-time-rule-trail">seconds</span>
                </div>
              </div>
            </Form.Field>
            {this.state.errors?.voice_time_threshold && (
              <div className="webhook-error-message" role="alert">
                {this.state.errors.voice_time_threshold}
              </div>
            )}
          </div>
        );
      }
      if (this.state.webhook_info.dynamicOptions[key]) {
        return (
          <div className="webhook-filter-rule" key={key}>
            <label className="webhook-filter-label">{`Webhook will trigger if ${key} option equals`}</label>
            <Form.Field>
              <Dropdown
                placeholder={`Choose ${key} value`}
                fluid
                selection
                multiple
                options={this.state.webhook_info.dynamicOptions[key]}
                value={this.state.webhook_info.selectedValues[key] || []}
                onChange={(e, d) => this.handleDynamicSelection(key, d.value)}
              />
            </Form.Field>
          </div>
        );
      }
      return null;
    });
  };
  web_renderDynamicDropdowns = () => {
    if (!this.state.webhook_info.web_selectedWebhook || !Array.isArray(this.state.webhook_info.web_selectedWebhook)) {
      return null;
    }
    return this.state.webhook_info.web_selectedWebhook.map((key) => {
      if (this.state.webhook_info.web_dynamicOptions[key]) {
        return (
          <div className="webhook-filter-rule" key={key}>
            <label className="webhook-filter-label">{`Webhook will trigger if ${key} option equals`}</label>
            <Form.Field>
              <Dropdown
                placeholder={`Choose ${key} value`}
                fluid
                selection
                multiple
                options={this.state.webhook_info.web_dynamicOptions[key]}
                value={this.state.webhook_info.web_selectedValues[key] || []}
                onChange={(e, d) => this.web_handleDynamicSelection(key, d.value)}
              />
            </Form.Field>
          </div>
        );
      }
      return null;
    });
  };
    
  // ✓ FIXED: Simplified and fixed typo (webhhok_info -> webhook_info)
  handleDynamicSelection = (key, value) => {
    const webhook_info = {
      ...this.state.webhook_info,
      selectedValues: {
        ...this.state.webhook_info.selectedValues,
        [key]: value
      }
    };
    this.setState({ webhook_info, disable: false });
  };

  web_handleDynamicSelection = (key, value) => {
    const webhook_info = {
      ...this.state.webhook_info,
      web_selectedValues: {
        ...this.state.webhook_info.web_selectedValues,
        [key]: value
      }
    };
    this.setState({ webhook_info, disable: false });
  };

  // ✓ REFACTORED: Use buildVoipDynamicOptions helper
  handleVoipWebhookRule = (e, d) => {
    // Filter out header items (they start with 'header-')
    const selectedKeys = d.value.filter(val => !val.startsWith('header-'));
    const dynamicOptions = buildVoipDynamicOptions(selectedKeys, this.state.voip_parsed_values);

    const webhook_info = {
      ...this.state.webhook_info,
      voip_selectedWebhook: selectedKeys,
      voip_dynamicOptions: dynamicOptions
    };

    this.setState({ webhook_info, disable: false });
  };

  // Render VOIP dynamic dropdowns
  voip_renderDynamicDropdowns = () => {
    return this.state.webhook_info.voip_selectedWebhook?.map((key) => {
      if (this.state.webhook_info.voip_dynamicOptions[key]) {
        return (
          <div className="webhook-filter-rule" key={key}>
            <label className="webhook-filter-label">{`Webhook will trigger if ${key} option equals`}</label>
            <Form.Field>
              <Dropdown
                placeholder={`Choose ${key} value`}
                fluid
                selection
                multiple
                options={this.state.webhook_info.voip_dynamicOptions[key]}
                value={this.state.webhook_info.voip_selectedValues[key] || []}
                onChange={(e, d) => this.voip_handleDynamicSelection(key, d.value)}
              />
            </Form.Field>
          </div>
        );
      }
      return null;
    });
  };

  // ✓ FIXED: Simplified and fixed typo
  voip_handleDynamicSelection = (key, value) => {
    const webhook_info = {
      ...this.state.webhook_info,
      voip_selectedValues: {
        ...this.state.webhook_info.voip_selectedValues,
        [key]: value
      }
    };
    this.setState({ webhook_info, disable: false });
  };

  // ✓ REFACTORED: Use deduplicateVoipFields helper with validation
  voip_branchOption = () => {
    if (!this.state.voip_parsed_values || !Array.isArray(this.state.voip_parsed_values)) {
      return [];
    }

    if (this.state.voip_parsed_values.length === 0) {
      return [];
    }

    try {
      const uniqueFields = deduplicateVoipFields(this.state.voip_parsed_values);

      if (!uniqueFields || uniqueFields.length === 0) {
        return [];
      }

      // Additional filter to ensure valid fields
      const options = uniqueFields
        .filter(field => field && field.key && typeof field.key === 'string' && field.key.trim() !== '')
        .map((field, index) => ({
          key: `voip-field-${index}`,
          text: field.key,
          value: field.key,
        }));

      // Add header as first item
      if (options.length > 0) {
        return [
          {
            key: 'header-voip',
            text: 'Name',
            value: 'header-voip',
            disabled: true,
            className: 'webhook-dropdown-header'
          },
          ...options
        ];
      }

      return options;
    } catch (err) {
      console.error('voip_branchOption: Error processing fields:', err);
      return [];
    }
  };
  getDataSource = (params) => {
    return api.webhook.loadWebhook(params).then((resp) => {
      if (resp && resp.list) {
        // Transform webhook URLs to include truncation, tooltip, and copy icon
        resp.list = resp.list.map((item) => {
          if (item.webhook_url) {
            item.webhook_url = this.formatWebhookUrl(item.webhook_url);
          }
          return item;
        });
      }
      return resp;
    });
  };

  // Format webhook URL with truncation, tooltip, and copy icon
  formatWebhookUrl = (urlHtml) => {
    // Extract the plain URL from HTML if it exists - using textContent for safety
    const tempDiv = document.createElement('div');
    tempDiv.textContent = urlHtml; // Set as text first
    let url = tempDiv.textContent || urlHtml;

    // If urlHtml appears to be HTML, try to extract text safely using DOMParser
    if (urlHtml.includes('<') || urlHtml.includes('>')) {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(urlHtml, 'text/html');
        url = doc.body.textContent || url;
      } catch (e) {
        // Fallback to treating it as plain text
        url = urlHtml.replace(/<[^>]*>/g, '');
      }
    }

    // Truncate URL if longer than 25 characters for compact display
    const maxLength = 25;
    const truncated = url.length > maxLength ? url.substring(0, maxLength) + '...' : url;

    // Escape HTML entities for safe rendering
    const escapeHtml = (str) => {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    };

    const escapedUrl = escapeHtml(url);
    const escapedTruncated = escapeHtml(truncated);
    const escapedUrlForJs = url.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/"/g, '\\"');

    // Return formatted HTML with tooltip and copy icon
    return `
      <div class="webhook-url-cell">
        <span
          class="webhook-url-text"
          title="${escapedUrl}"
        >
          ${escapedTruncated}
        </span>
        <i
          class="copy icon"
          onclick="window.copyWebhookUrl('${escapedUrlForJs}')"
          title="Copy URL"
        ></i>
      </div>
    `;
  }; 
   deleteCredentials = () => {
            this.setState({confirmDialog:false});
            api.webhook
                  .deleteWebhook({ id: this.state.webhook_id })
                  .then((resp) => {
                      
                      if (resp && resp.message) {
                         
                          this.setState({ successMessage: resp.message,  loading: false,
                          webhook_id: 0, refreshGrid:true});
                         
                          utilFunc.scrollTop(800);
                          setTimeout(() => {
                              this.setState({ 
                                successMessage: null,
                                loading: false,
                               webhook_id: 0,refreshGrid:false });
                          }, 4000);
                      } else if (resp.errorMessage) {
                        this.setState({  errorMessage: resp.errorMessage,  loading: false,
                          webhook_id: 0, });
                         
                      }
                  })
                  .catch((err) => {
                      console.log(err);
                      this.setState({ 
                         loading: false,
                        webhook_id: 0, });
                  });
          
      };
  
  // ✓ REFACTORED: Simplified editCredentials using helper functions
  editCredentials = async (id) => {
    this.setState({ fallback_webhook_exists: false });

    if (!id) return;

    let resp = null;

    try {
      resp = await api.webhook.getWebhookInfo({ id });
      if (!resp || !resp.webhhok_info) return;

      const res = await api.manageUser.getVoiceWorkspaceBranch({});
      if (!res.status) return;

      // ✓ Use centralized helper instead of duplicating logic
      const voip_parsed_values = await fetchVoipParsedValues();

      const parsed_values = res.data?.parsed_values || [];
      const web_parsed_values = res.data?.webparsedetail?.parsed_values || [];

      // ✓ REFACTORED: Use helper functions to process filters
      const voiceFilters = processFiltersForEdit(resp.webhhok_info.filters, parsed_values, "voice");
      const webFilters = processFiltersForEdit(resp.webhhok_info.web_filters, web_parsed_values, "web");
      const voipFilters = processVoipFiltersForEdit(resp.webhhok_info.voip_filters, voip_parsed_values);

      // ✓ FIXED: Spread webhhok_info FIRST, then override specific fields
      const webhook_info = {
        ...resp.webhhok_info,
        selectedWebhook: voiceFilters.selectedWebhook,
        dynamicOptions: voiceFilters.dynamicOptions,
        selectedValues: voiceFilters.selectedValues,
        web_selectedWebhook: webFilters.selectedWebhook,
        web_dynamicOptions: webFilters.dynamicOptions,
        web_selectedValues: webFilters.selectedValues,
        voip_selectedWebhook: voipFilters.selectedWebhook,
        voip_dynamicOptions: voipFilters.dynamicOptions,
        voip_selectedValues: voipFilters.selectedValues
      };

      // Hydrate outbound voice state from saved data
      const outboundVoiceOn = normalizeBoolean(resp.webhhok_info.outbound_voice_on);
      let savedOutboundFilters = parseFilters(resp.webhhok_info.outbound_filters, "outbound");

      if (outboundVoiceOn || savedOutboundFilters.length > 0) {
        const normalizeOutboundWorkspaceId = (id) => {
          if (id === null || id === undefined) return id;
          const n = Number(id);
          return Number.isNaN(n) ? id : n;
        };

        const selectedWorkspaces = savedOutboundFilters.map((ws) =>
          normalizeOutboundWorkspaceId(ws.workspace_id)
        );
        const parsedFields = {};
        const selectedValuesOutbound = {};
        const requiredSettings = {};

        savedOutboundFilters.forEach((ws) => {
          const wid = normalizeOutboundWorkspaceId(ws.workspace_id);
          parsedFields[wid] = (ws.fields || []).map((f) => f.field);

          selectedValuesOutbound[wid] = {};
          (ws.fields || []).forEach((f) => {
            if (f.selectedValue && f.selectedValue.length > 0) {
              selectedValuesOutbound[wid][f.field] = f.selectedValue;
            }
          });

          requiredSettings[wid] = normalizeBoolean(ws.required_setting);
        });

        this.props.hydrateOutbound({
          enabled: outboundVoiceOn,
          selectedWorkspaces,
          parsedFields,
          selectedValues: selectedValuesOutbound,
          requiredSettings,
        });
      } else {
        this.props.resetOutbound();
      }

      const outboundSmsOn = normalizeBoolean(resp.webhhok_info.outbound_sms_on);
      const savedOutboundSmsFilters = parseFilters(
        resp.webhhok_info.outbound_sms_filters,
        "outbound"
      );

      if (outboundSmsOn || savedOutboundSmsFilters.length > 0) {
        const normalizeSmsWorkspaceId = (id) => {
          if (id === null || id === undefined) return id;
          const n = Number(id);
          return Number.isNaN(n) ? id : n;
        };

        const smsSelectedWorkspaces = savedOutboundSmsFilters.map((ws) =>
          normalizeSmsWorkspaceId(ws.workspace_id)
        );
        const smsParsedFields = {};
        const smsSelectedValues = {};
        const smsRequiredSettings = {};

        savedOutboundSmsFilters.forEach((ws) => {
          const wid = normalizeSmsWorkspaceId(ws.workspace_id);
          smsParsedFields[wid] = (ws.fields || [])
            .map((f) => f.field)
            .filter((f) => f !== WEBHOOK_TIME_FILTER_FIELD);
          smsSelectedValues[wid] = {};
          (ws.fields || []).forEach((f) => {
            if (
              f.field !== WEBHOOK_TIME_FILTER_FIELD &&
              f.selectedValue &&
              f.selectedValue.length > 0
            ) {
              smsSelectedValues[wid][f.field] = f.selectedValue;
            }
          });
          smsRequiredSettings[wid] = normalizeBoolean(ws.required_setting);
        });

        this.props.hydrateOutboundSms({
          enabled: outboundSmsOn,
          selectedWorkspaces: smsSelectedWorkspaces,
          parsedFields: smsParsedFields,
          selectedValues: smsSelectedValues,
          requiredSettings: smsRequiredSettings,
        });
      } else {
        this.props.resetOutboundSms();
      }

      // ✓ FIXED: Set all state in a single setState call
      this.setState({
        workspaceId: res.data.workspaceId,
        workspaceBranches: res.data.data,
        voice_type: res.data?.type,
        parsed_values,
        web_workspaceId: res.data?.webparsedetail?.workspaceId,
        web_workspaceBranches: res.data?.webparsedetail?.data,
        web_parsed_values,
        voip_parsed_values,
        voice_indices: res.data?.key_indices || [],
        web_indices: res.data?.webparsedetail?.key_indices || [],
        webhook_info,
        webhook_id: id,
        modal: true
      });

      this.createNew(false);
    } catch (err) {
      console.error("Error in editCredentials:", err);
      // ✓ FIXED: Only use resp if it was successfully fetched
      if (resp && resp.webhhok_info) {
        this.setState({
          webhook_info: resp.webhhok_info,
          webhook_id: id,
          modal: true
        });
        this.createNew(false);
      }
    }
  };

  // ✓ REFACTORED: Simplified createNew using helper functions
  createNew = async (old) => {
    this.setState({ fallback_webhook_exists: false });

    if (!old) {
      this.setState({ modal: true });
      return;
    }

    try {
      const res = await api.manageUser.getVoiceWorkspaceBranch({});
      if (!res.status) return;

      // ✓ Use centralized helper instead of duplicating logic
      const voip_parsed_values = await fetchVoipParsedValues();

      this.setState({
        workspaceId: res.data.workspaceId,
        workspaceBranches: res.data.data,
        voice_type: res.data?.type,
        parsed_values: res.data?.parsed_values || [],
        web_workspaceId: res.data?.webparsedetail?.workspaceId,
        web_workspaceBranches: res.data?.webparsedetail?.data,
        web_parsed_values: res.data?.webparsedetail?.parsed_values || [],
        voip_parsed_values,
        voice_indices: res.data?.key_indices || [],
        web_indices: res.data?.webparsedetail?.key_indices || [],
      }, () => {
        // ✓ FIXED: Use consistent naming (webhook_info) and simplified object creation
        const webhook_info = {
          status: true,
          webhook_url: "",
          required_settings: 0,
          contentType: "multipart/form-data",
          selectedWebhook: [],
          dynamicOptions: {},
          selectedValues: {},
          web_selectedWebhook: [],
          web_dynamicOptions: {},
          web_selectedValues: {},
          voip_selectedWebhook: [],
          voip_dynamicOptions: {},
          voip_selectedValues: {},
          fallback_webhook: 0
        };

        this.setState({ webhook_info, webhook_id: 0, modal: true });
      });
    } catch (err) {
      console.error("Error in createNew:", err);
    }
  };
  
  // ✓ FIXED: Consistent naming (webhook_info)
  clearState = (data = null) => {
    return new Promise((resolve) => {
      let elem = {
        modal: false,
        webhook_id: 0,
        webhook_info: {},
        errorMessage: null,
        successMessage: null,
        errors: null,
      };

      if (data) {
        elem = { ...elem, ...data };
      }

      // Reset outbound voice state
      this.props.resetOutbound();
      this.props.resetOutboundSms();
      this.setState(elem, () => {
        resolve();
      });
    });
  };
    toggleModal = () => this.clearState();

  submitForm = () => this.formRef.current.submit();


    createCredentials = () => {
      const errorLabel = <div className="red" pointing={"true"} />;
    const options = [
      { key: 'json', text: 'JSON', value: 'application/json' },
      { key: 'form', text: 'Form Data', value: 'multipart/form-data' },
    ];
      return (
        <div
          className={this.state.modal ? "modal fade show " : "modal fade"}
          data-backdrop="static"
          tabIndex="-1"
          role="dialog"
          aria-labelledby="staticBackdrop"
          aria-hidden="true"
          style={{ display: this.state.modal ? "block" : "none" }}
        >
          {this.state.formLoading && <div className="ui loading form"></div>}
          {this.state.successMessage && <div className="ui success message">{this.state.successMessage}</div>}
          
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <button
                type="button"
                className="close linear-gradient"
                data-dismiss="modal"
                aria-label="Close"
                onClick={this.toggleModal} 
              >
                X
              </button>
              <div className="modal-header">
                <h5 className="modal-title pl-0" style={{marginLeft:'18px'}}>{"Webhook"}</h5>
              </div>
              <div className="modal-body webhookform">
              <div className="webhook-section-card">
                <div className="webhook-section-header">
                  <div className="webhook-section-title">
                    <label className="webhook-main-label">Enable Webhook</label>
                    <Switch
                      onChange={this.handleSwitch}
                      checked={this.state.webhook_info.status==true || this.state.webhook_info.status=='active' ? true: false}
                      onColor="#86d3ff"
                      onHandleColor="#2693e6"
                      handleDiameter={30}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                      activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                      height={20}
                      width={48}
                      className="react-switch"
                      id="material-switch"
                    />
                  </div>
                </div>
                <div className="webhook-card-content">
                  <p className="webhook-section-description">Enable or disable all webhook events.</p>
                  {(this.state.errors && (this.state.webhook_info.status==false || this.state.webhook_info.status=='inactive')) && (
                    <div className="webhook-error-message" style={{marginTop: '10px'}} ref={this.errorRef}>
                      <p className="text-danger" style={{margin: 0}}>Enable and enter a webhook URL to save.</p>
                    </div>
                  )}
                </div>
              </div>
                 <Form  
                  noValidate
                  autoComplete="off"
                  onValidSubmit={this.handleSubmit}
                  className="kt-form kt-form--label-right"
                  ref={this.formRef}
                  style={{ fontFamily:'Poppins", sans-serif' }}
                  id="webhookinfo-form"
                >
                 {(this.state.webhook_info.status==true || this.state.webhook_info.status=='active') && <React.Fragment>
                   <div className="webhook-section-card">
                    <div className="webhook-card-content">
                      <div className="webhook-input-group">
                        <label className="webhook-input-label">
                          Webhook Url <span style={{color: 'red'}}>*</span>
                        </label>
                        <Form.Input
                          type="text"
                          autoComplete="off"
                          name="webhook_url"
                          placeholder="Enter webhook URL"
                          className="webhook-url-input"
                          value={this.state.webhook_info.webhook_url}
                          onChange={this.handleChange}
                          error={
                            this.state.errors && this.state.errors.webhook
                              ? true
                              : false
                          }
                        />
                        {this.state.errors?.webhook && (
                          <div className="webhook-error-message" ref={this.errorRef}>
                            {this.state.errors.webhook}
                          </div>
                        )}
                      </div>
                      <div className="webhook-input-group">
                        <Form.Field>
                          <label className="webhook-input-label">Content Type</label>
                          <Dropdown
                            placeholder="Select Content Type"
                            fluid
                            selection
                            options={options}
                            onChange={this.handleDropdownChange}
                            value={this.state.webhook_info.content_type}
                          />
                        </Form.Field>
                      </div>
                    </div>
                   </div>
                      <div className="webhook-section-card">
                        <div className="webhook-section-header">
                          <div className="webhook-section-title">
                            <label className="webhook-main-label">Inbound Voice</label>
                            <Switch
                              onChange={this.handleSwitchvoice}
                              checked={
                                this.state.webhook_info.voice_on === true ||
                                this.state.webhook_info.voice_on === "1" ||
                                this.state.webhook_info.voice_on === "on"
                              }
                              onColor="#86d3ff"
                              onHandleColor="#2693e6"
                              handleDiameter={30}
                              uncheckedIcon={false}
                              checkedIcon={false}
                              boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                              activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                              height={20}
                              width={48}
                              className="react-switch"
                              id="material-switch"
                            />
                          </div>
                        </div>

                        {(this.state.webhook_info.fallback_webhook==0 && (this.state.webhook_info.voice_on=='1' || this.state.webhook_info.voice_on==true)) && (
                          <div className="webhook-card-content">
                            {this.state.webhook_info.voice_on && this.state.parsed_values && (
                              <div className="webhook-filter-rule">
                                <label className="webhook-filter-label">Add webhook filtering rule</label>
                                <Form.Field>
                                  <Dropdown
                                    placeholder="Searchable Select Rule"
                                    fluid
                                    multiple
                                    search
                                    selection
                                    icon="search"
                                    options={this.branchOption()}
                                    onChange={this.handleWebhookRule}
                                    value={this.state.webhook_info.selectedWebhook || []}
                                    className="webhook-searchable-dropdown"
                                  />
                                </Form.Field>
                              </div>
                            )}
                            {this.renderDynamicDropdowns()}
                            <div className="webhook-checkbox-container">
                              <Checkbox
                                onChange={(e, data)=>this.HandleRequiredSetting(data.checked)}
                                checked={this.state.webhook_info.required_setting==1 || this.state.webhook_info.required_setting==true ? true: false}
                              />
                              <label className="webhook-checkbox-label" onClick={() => this.HandleRequiredSetting(!(this.state.webhook_info.required_setting==1 || this.state.webhook_info.required_setting==true))}>
                                Use the "if all required fields are gathered" rule within the workspace to determine when to pass a payload
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                      <OutboundVoiceWebhook
                        showOutbound={this.state.webhook_info.fallback_webhook}
                        formErrors={this.state.errors}
                        onClearFormErrorKey={this.clearFormErrorKey}
                      />
                      <OutboundSmsWebhook
                        showOutbound={this.state.webhook_info.fallback_webhook}
                      />

                 <div className="webhook-section-card">
                  <div className="webhook-section-header">
                    <div className="webhook-section-title">
                      <label className="webhook-main-label">Chat</label>
                      <Switch
                        onChange={this.handleSwitchweb}
                        checked={
                          this.state.webhook_info.web_on === true ||
                          this.state.webhook_info.web_on === "1" ||
                          this.state.webhook_info.web_on === "on"
                        }
                        onColor="#86d3ff"
                        onHandleColor="#2693e6"
                        handleDiameter={30}
                        uncheckedIcon={false}
                        checkedIcon={false}
                        boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                        activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                        height={20}
                        width={48}
                        className="react-switch"
                        id="material-switch"
                      />
                    </div>
                  </div>

                  {(this.state.webhook_info.fallback_webhook==0 && (this.state.webhook_info.web_on=='1' || this.state.webhook_info.web_on==true)) && (
                    <div className="webhook-card-content">
                      {this.state.web_parsed_values && (
                        <div className="webhook-filter-rule">
                          <label className="webhook-filter-label">Add webhook filtering rule</label>
                          <Form.Field>
                            <Dropdown
                              placeholder="Searchable Select Rule"
                              fluid
                              multiple
                              search
                              selection
                              icon="search"
                              options={this.web_branchOption()}
                              onChange={this.handleweb_WebhookRule}
                              value={this.state.webhook_info.web_selectedWebhook || []}
                              className="webhook-searchable-dropdown"
                            />
                          </Form.Field>
                        </div>
                      )}
                      {this.web_renderDynamicDropdowns()}
                      <div className="webhook-checkbox-container">
                        <Checkbox
                          onChange={(e, data)=>this.HandleRequiredSettingforweb(data.checked)}
                          checked={this.state.webhook_info.web_required_setting==1 || this.state.webhook_info.web_required_setting==true ? true: false}
                        />
                        <label className="webhook-checkbox-label" onClick={() => this.HandleRequiredSettingforweb(!(this.state.webhook_info.web_required_setting==1 || this.state.webhook_info.web_required_setting==true))}>
                          Use the "if all required fields are gathered" rule within the workspace to determine when to pass a payload
                        </label>
                      </div>
                    </div>
                  )}
                 </div>

{/* webhook for voip */}
                  <div className="webhook-section-card">
                    <div className="webhook-section-header">
                      <div className="webhook-section-title">
                        <label className="webhook-main-label">VOIP</label>
                        <Switch
                          onChange={this.handleSwitchVoip}
                          checked={
                            this.state.webhook_info.voip_on === true ||
                            this.state.webhook_info.voip_on === "1" ||
                            this.state.webhook_info.voip_on === "on"
                          }
                          onColor="#86d3ff"
                          onHandleColor="#2693e6"
                          handleDiameter={30}
                          uncheckedIcon={false}
                          checkedIcon={false}
                          boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                          activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                          height={20}
                          width={48}
                          className="react-switch"
                          id="material-switch"
                        />
                      </div>
                    </div>

                    {(this.state.webhook_info.fallback_webhook==0 && (this.state.webhook_info.voip_on=='1' || this.state.webhook_info.voip_on==true)) && (
                      <div className="webhook-card-content">
                        {this.state.webhook_info.voip_on && this.state.voip_parsed_values && (
                          <div className="webhook-filter-rule">
                            <label className="webhook-filter-label">Add webhook filtering rule</label>
                            <Form.Field>
                              <Dropdown
                                placeholder={this.state.voip_parsed_values.length === 0 ? "No VOIP parse fields configured" : "Searchable Select Rule"}
                                fluid
                                multiple
                                search
                                selection
                                icon="search"
                                options={this.voip_branchOption()}
                                onChange={this.handleVoipWebhookRule}
                                value={this.state.webhook_info.voip_selectedWebhook || []}
                                disabled={this.state.voip_parsed_values.length === 0}
                                className="webhook-searchable-dropdown"
                              />
                            </Form.Field>
                            {this.state.voip_parsed_values.length === 0 && (
                              <div style={{color: '#f2711c', marginTop: '5px', fontSize: '12px'}}>
                                Please configure VOIP parse settings first to enable webhook filtering rules.
                              </div>
                            )}
                          </div>
                        )}
                        {this.voip_renderDynamicDropdowns()}
                        <div className="webhook-checkbox-container">
                          <Checkbox
                            onChange={(e, data)=>this.HandleRequiredSettingforvoip(data.checked)}
                            checked={this.state.webhook_info.voip_required_setting==1 || this.state.webhook_info.voip_required_setting==true ? true: false}
                          />
                          <label className="webhook-checkbox-label" onClick={(e) => {
                            if (e.target.tagName !== 'A') {
                              this.HandleRequiredSettingforvoip(!(this.state.webhook_info.voip_required_setting==1 || this.state.webhook_info.voip_required_setting==true));
                            }
                          }}>
                            Use the "if all required fields are gathered" rule within the{' '}
                            <a
                              href={`${config.baseUrl}/v2/voip`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{color: '#2693e6', textDecoration: 'underline'}}
                              onClick={(e) => e.stopPropagation()}
                            >
                              VOIP settings
                            </a>
                            {' '}to determine when to pass a payload
                          </label>
                        </div>
                      </div>
                    )}
                  </div>

               <div className="webhook-section-card">
                <div className="webhook-card-content">
                  <div className="webhook-checkbox-container">
                    <Checkbox
                      onChange={(e, data)=>this.fallbackWebhook(data.checked)}
                      checked={this.state.webhook_info.fallback_webhook==1 || this.state.webhook_info.fallback_webhook==true ? true: false}
                    />
                    <label className="webhook-checkbox-label" onClick={() => this.fallbackWebhook(!(this.state.webhook_info.fallback_webhook==1 || this.state.webhook_info.fallback_webhook==true))}>
                      Fallback Webhook - Trigger only when no other account webhooks fire
                    </label>
                  </div>
                  {this.state.fallback_webhook_exists && (
                    <div style={{marginTop: '10px'}}>
                      <p className="text-danger" style={{margin: 0}}>Fallback webhook already exists.</p>
                    </div>
                  )}
                </div>
               </div>
               
                 </React.Fragment> }
                <div className="form-group row" style={{marginTop:'10px'}}>
                  <div class="col-lg-12 col-md-12 col-sm-12 form-group-sub ">
                    <button
                      type="submit"
                      disabled={this.state.disable}
                      style={{ float: "right" }}
                      className="btn linear-gradient yr-submitbtn btn-sm"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </Form>
              </div>
             
            </div>
          </div>
        </div>
      );
      

      
    };

  render() {
    
    return (
      <React.Fragment>
        <div className="row" id="webhookportal">
          <div className="col-lg-12 col-md-12 col-sm-12">
            {this.state.modal && this.createCredentials()}
            {this.state.confirmDialog && (
              <ConfirmDialog
                show={this.state.confirmDialog}
                toggleModal={this.deleteCredentials}
                closeAction={this.toggleConfirmModal}
                title="Confirm your action"
                message={"Do you really want to delete this credential ?"}
              />
            )}         
                       {this.state.loading && <div className="ui loading form"></div>}
            <div className="kt-portlet__body kt-portlet__body--fit ">
            {this.state.successMessage && (
                         <Message color={"green"}>{this.state.successMessage}</Message>
                       )}
                       {this.state.errorMessage && (
                         <Message color={"red"}>{this.state.errorMessage}</Message>
                       )}
              <div class="form-group row">
                <div class="col-lg-12 col-md-12 col-sm-12 form-group-sub ">
                  <button
                    type="submit"
                    style={{ float: "center" }}
                    className="btn linear-gradient yr-submitbtn btn-sm"
                    onClick={this.openPage}
                  >
                    Webhook Tutorial Docs
                  </button>
                
                </div>
              </div>
              <div className="yr-manageUser-table">
                        <Segment>
                            <div id="webhook-list-table" className="webhook-integration act--integration webhook-list-table">
                                <GridList
                                    dataSource={(params) => this.getDataSource(params)}
                                    dataColumns={this.columnLists}
                                    refreshGrid={this.state.refreshGrid}
                                >
                                    <div className="col-md-8 kt-margin-b-20-tablet-and-mobile">
                                        <button
                                            style={{ marging: "0 0 15px 0", float: "right" }}
                                            type="button"
                                            className="btn linear-gradient yr-submitbtn"
                                            onClick={()=>this.createNew(true)}
                                        >
                                            Create New
                                        </button>
                                    </div>
                                </GridList>
                            </div>
                        </Segment>
                    </div>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}
  /**
   * Build outbound webhook filters payload
   *
   * INPUT:
   * - outboundVoice (Redux slice)
   *
   * OUTPUT:
   * - Array of workspace-level outbound webhook rules
   *
   * Structure:
   * [
   *   {
   *     workspace_id,
   *     workspace_name,
   *     required_setting,
   *     fields: [
   *       { field, selectedValue }
   *     ]
   *   }
   * ]
   *
   * Used during webhook SAVE to send normalized outbound data to backend
   * Returns empty array if data is invalid
   */
 const buildOutboundFilters = (outboundVoice) => {
    // Validate input
    if (!outboundVoice || !outboundVoice.enabled) {
      return [];
    }

    if (!outboundVoice.selectedWorkspaces || !Array.isArray(outboundVoice.selectedWorkspaces)) {
      console.warn('buildOutboundFilters: selectedWorkspaces is invalid');
      return [];
    }

    if (outboundVoice.selectedWorkspaces.length === 0) {
      return [];
    }

    // Create a quick lookup for workspace names (with validation)
    const workspaceMap = {};
    if (outboundVoice.workspaces && Array.isArray(outboundVoice.workspaces)) {
      outboundVoice.workspaces.forEach((w) => {
        if (w && w.workspace_id && w.workspace_name) {
          workspaceMap[w.workspace_id] = w.workspace_name;
        }
      });
    }

    // Build filters for each selected workspace
    return outboundVoice.selectedWorkspaces
      .filter(wsId => wsId !== null && wsId !== undefined)
      .map((wsId) => {
        // Get parsed fields with validation
        const parsedFields = outboundVoice.parsedFields?.[wsId];
        const fields = Array.isArray(parsedFields) ? parsedFields : [];

        return {
          workspace_id: wsId,
          workspace_name: workspaceMap[wsId] || "",

          // Convert boolean → integer for backend consistency
          required_setting: outboundVoice.requiredSettings?.[wsId] ? 1 : 0,

          // Workspace-specific filter rules
          fields: fields
            .filter(field => field && typeof field === 'string' && field.trim() !== '')
            .map((field) => {
              const selectedValue = outboundVoice.selectedValues?.[wsId]?.[field] || [];
              return {
                field,
                selectedValue,
              };
            }),
        };
      });
  }; 

/**
 * outboundVoice (from Redux)
 *
 * Holds Outbound Voice Webhook configuration:
 * - enabled: whether outbound voice is ON
 * - workspaces: available outbound workspaces
 * - selectedWorkspaces: chosen workspaces for outbound
 * - parsedFields: selected filter fields per workspace
 * - selectedValues: selected values per field per workspace
 * - requiredSettings: "all required fields gathered" rule per workspace
 */
const mapStateToProps = (state) => ({
  outboundVoice: state.outboundVoice,
  outboundSms: state.outboundSms,
});

/**
 * Map Redux actions to component props
 *
 * These actions can be called directly from props:
 * - resetOutbound():
 *   Clears outbound voice configuration (used on modal close / cancel)
 *
 * - hydrateOutbound(payload):
 *   Prefills outbound state when editing an existing webhook
 */
const mapDispatchToProps = {
  resetOutbound,
  hydrateOutbound,
  resetOutboundSms,
  hydrateOutboundSms,
};

/**
 * Connect Redux state and actions to Webhook component
 *
 * This allows Webhook to:
 * - Read outboundVoice state
 * - Reset or hydrate outbound configuration when needed
 */
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Webhook);
